import { extractApiError, type HttpApiClient } from "@vana/core";
import { type CaptureType, type KycCapture } from "../../domain/entities/kyc-capture";
import { type KycCustomer } from "../../domain/entities/kyc-customer";
import {
  ListCapturesError,
  RemoveCaptureError,
  UploadCaptureError,
} from "../../domain/errors/errors";
import { type KycRepository } from "../../domain/repos/kyc-repo";
import {
  type CaptureResponse,
  type CapturesResponse,
} from "../../infrastructure/responses/captures-response";
import { type RequestCustomerResponse } from "../../infrastructure/responses/request-customer-response";

/** Total upload attempts (1 initial + retries) — resilience on flaky field networks. */
const UPLOAD_ATTEMPTS = 3;
const RETRY_DELAY_MS = 600;

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export class KycRepo implements KycRepository {
  constructor(private readonly apiClient: HttpApiClient) {}

  async getCustomer(requestId: string): Promise<KycCustomer> {
    // Never block the screen on it: without a name or DPI the ambassador still
    // compares what the customer shows them and carries on (DMS-71).
    try {
      const response = await this.apiClient.get<RequestCustomerResponse>(
        `v1/requests/${requestId}`,
      );
      const c = response.data?.customer;
      if (!c) return { name: "", nationalId: "" };
      return {
        name: [c.firstName, c.middleName, c.lastName, c.secondLastName]
          .filter((p): p is string => Boolean(p))
          .join(" "),
        nationalId: c.nationalId ?? "",
      };
    } catch {
      return { name: "", nationalId: "" };
    }
  }

  async listCaptures(requestId: string): Promise<KycCapture[]> {
    try {
      const response = await this.apiClient.get<CapturesResponse>(
        `v1/requests/${requestId}/captures`,
      );
      return response.data?.items ?? [];
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new ListCapturesError(message, apiError);
    }
  }

  async uploadCapture(requestId: string, type: CaptureType, image: Blob): Promise<KycCapture> {
    const url = `v1/requests/${requestId}/captures`;
    let lastError: unknown;

    for (let attempt = 1; attempt <= UPLOAD_ATTEMPTS; attempt++) {
      try {
        // A fresh FormData per attempt (a consumed body can't be re-sent).
        const form = new FormData();
        form.append("type", type);
        form.append("file", image, `${type}.jpg`);

        const response = await this.apiClient.post<FormData, CaptureResponse>(url, form);
        if (!response.data) throw new UploadCaptureError("Empty upload response");
        return response.data;
      } catch (error) {
        lastError = error;
        if (attempt < UPLOAD_ATTEMPTS) await delay(RETRY_DELAY_MS * attempt);
      }
    }

    const { message, apiError } = extractApiError(lastError);
    throw new UploadCaptureError(message, apiError);
  }

  async removeCapture(requestId: string, type: CaptureType, page = 1): Promise<void> {
    try {
      await this.apiClient.delete(`v1/requests/${requestId}/captures/${type}?page=${page}`);
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new RemoveCaptureError(message, apiError);
    }
  }
}
